import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutTemplate, Plus, Trash2 } from 'lucide-react';

import { useCMSContentTypesQuery, useCMSTemplatesQuery } from '@/client/api/cms/cms.query';
import {
  useApplyTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useUpdateTemplateMutation,
} from '@/client/api/cms/cms.mutation';
import { CMSTemplate } from '@/client/types/cms';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const blank = {
  name: '',
  name_system: '',
  ctype: '',
  title_tpl: '',
  summary: '',
  content: '',
};

const TemplatesPage = () => {
  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Templates' },
  ]);

  const navigate = useNavigate();
  const { data, isLoading } = useCMSTemplatesQuery();
  const { data: types } = useCMSContentTypesQuery();
  const createMutation = useCreateTemplateMutation();
  const updateMutation = useUpdateTemplateMutation();
  const deleteMutation = useDeleteTemplateMutation();
  const applyMutation = useApplyTemplateMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CMSTemplate | null>(null);
  const [form, setForm] = useState(blank);

  const templates = data ?? [];

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (t: CMSTemplate) => {
    setEditing(t);
    setForm({
      name: t.name,
      name_system: t.name_system ?? '',
      ctype: t.ctype ?? '',
      title_tpl: t.title_tpl ?? '',
      summary: t.summary ?? '',
      content: t.content ?? '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = {
      name: form.name.trim(),
      name_system: form.name_system.trim() || undefined,
      ctype: form.ctype || null,
      title_tpl: form.title_tpl || null,
      summary: form.summary || null,
      content: form.content || null,
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ ctplid: editing.ctplid, ...body });
        toast.success('Template updated');
      } else {
        await createMutation.mutateAsync(body);
        toast.success('Template created');
      }
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleApply = async (t: CMSTemplate) => {
    try {
      const page = await applyMutation.mutateAsync({
        ctplid: t.ctplid,
        overrides: { title: t.title_tpl || t.name },
      });
      toast.success('Draft page created');
      if (page?.cmsoid) navigate(`/admin/content/pages/${page.cmsoid}`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Apply failed');
    }
  };

  const handleDelete = async (t: CMSTemplate) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(t.ctplid);
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <LayoutTemplate className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Reusable scaffolds. Apply creates a draft page.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          New template
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Type', 'Title template', ''].map((h) => (
                <th key={h || 'a'} className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {[...Array(4)].map((__, j) => (
                    <td key={j} className="px-6 py-4">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading && templates.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-16 text-center text-sm text-gray-500">
                  No templates yet.
                </td>
              </tr>
            )}
            {!isLoading &&
              templates.map((t) => (
                <tr key={t.ctplid} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button className="text-sm font-medium text-gray-900 hover:text-blue-700" onClick={() => openEdit(t)}>
                      {t.name}
                    </button>
                    {t.name_system && <p className="font-mono text-xs text-gray-500">{t.name_system}</p>}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">{t.ctype ?? '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{t.title_tpl ?? '—'}</td>
                  <td className="space-x-2 px-6 py-4 text-right">
                    <Button size="sm" onClick={() => handleApply(t)} disabled={applyMutation.isPending}>
                      Apply
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(t)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit template' : 'New template'}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>System name</Label>
              <Input
                value={form.name_system}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name_system: e.target.value.replace(/\s+/g, '_').toLowerCase() }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Content type</Label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={form.ctype}
                onChange={(e) => setForm((p) => ({ ...p, ctype: e.target.value }))}
              >
                <option value="">Any</option>
                {(types ?? []).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Title template</Label>
              <Input value={form.title_tpl} onChange={(e) => setForm((p) => ({ ...p, title_tpl: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Summary</Label>
              <textarea
                className="min-h-[60px] w-full rounded-md border px-3 py-2 text-sm"
                value={form.summary}
                onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Content HTML</Label>
              <textarea
                className="min-h-[120px] w-full rounded-md border px-3 py-2 font-mono text-xs"
                value={form.content}
                onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              />
            </div>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
